<script setup>
/**
 * "Request top-up" (spec §5 P-5.3): the one button on the credit card, and the status fix
 * `request_top_up`. Billing is manual, so it ASKS US. The sidecar mails the operator once per
 * top-up and says so when we were already told (a low-balance alert, or an earlier press since
 * the last top-up).
 *
 * States: idle, sending, requested, told, failed. `alreadyTold` from the credit read starts it in
 * `told`, disabled, because pressing again could change nothing. A failure leaves it pressable.
 */
import { computed, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import { useWasloI18n } from '../i18n.js';
import { wasloApi } from '../api/client.js';

const props = defineProps({
  accountId: { type: Number, default: null },
  alreadyTold: { type: Boolean, default: false },
});

const emit = defineEmits(['requested']);

const { t } = useWasloI18n();

const state = ref(props.alreadyTold ? 'told' : 'idle');
watch(
  () => props.alreadyTold,
  told => {
    if (told && state.value !== 'requested') state.value = 'told';
  }
);

const LABEL = {
  idle: 'WASLO.OVERVIEW.CREDITS.REQUEST',
  failed: 'WASLO.OVERVIEW.CREDITS.REQUEST',
  sending: 'WASLO.OVERVIEW.CREDITS.SENDING',
  requested: 'WASLO.OVERVIEW.CREDITS.REQUESTED',
  told: 'WASLO.OVERVIEW.CREDITS.TOLD',
};
const label = computed(() => t(LABEL[state.value]));
const done = computed(
  () => state.value === 'requested' || state.value === 'told'
);

const request = async () => {
  if (props.accountId === null || state.value === 'sending' || done.value) {
    return;
  }
  state.value = 'sending';
  try {
    const answer = await wasloApi.requestTopUp(props.accountId);
    state.value = answer.alreadyTold ? 'told' : 'requested';
    emit('requested', answer);
  } catch {
    state.value = 'failed';
  }
};
</script>

<template>
  <div
    class="flex flex-col items-start gap-1"
    data-waslo-top-up
    :data-waslo-top-up-state="state"
    aria-live="polite"
  >
    <Button
      :label="label"
      size="sm"
      variant="faded"
      color="slate"
      :is-loading="state === 'sending'"
      :disabled="done"
      data-waslo-top-up-button
      @click="request"
    />
    <p
      v-if="state === 'requested'"
      class="mb-0 text-sm text-n-teal-11"
      data-waslo-top-up-sent
    >
      {{ t('WASLO.OVERVIEW.CREDITS.SENT') }}
    </p>
    <p
      v-if="state === 'failed'"
      class="mb-0 text-sm text-n-ruby-11"
      data-waslo-top-up-error
    >
      {{ t('WASLO.OVERVIEW.CREDITS.REQUEST_FAILED') }}
    </p>
  </div>
</template>
